/* A tiny chainable async interface with class-based steps and integrated ledger.
 * Features:
 *  - ClassName.then(NextClass) for sequential chaining
 *  - Multiple .when(condition, TargetClass, label?) for branching (evaluated in order)
 *  - ClassName.invoke(initialState) to execute the chain (internal runner)
 *  - ClassName.graph({ direction?: 'TD'|'LR' }) -> Mermaid flowchart
 *  - Optional retries via .retries(n), and error routing via .catch(ErrorClass)
 *  - Automatic ledger integration for operation tracking and race condition detection
 */

import { createLedger, LEDGER_SYMBOL, reconcileLedger, type LedgerLog } from './ledger.js';

export type State = Record<string, any>;

export type StepClass<S extends State = State> =
  (new () => ChainStep<S>) &
  typeof ChainStep<S>;

type Branch<S extends State> = {
  cond: (state: S) => boolean;
  target: StepClass<S>;
  label?: string;
};

export abstract class ChainStep<S extends State> {
  // ----- Static chain definition storage (per subclass) -----
  // These are per-class; assigning via static methods writes onto the subclass constructor.
  protected static _next?: StepClass<any>;
  protected static _branches?: Branch<any>[];
  protected static _maxRetries?: number;
  protected static _onError?: StepClass<any>;

  // ----- Implement in subclasses -----
  abstract execute(state: S): Promise<S>;

  // ----- Chain definition DSL -----
  static then<S extends State>(this: StepClass<S>, next: StepClass<S> | StepClass<S>[]): StepClass<S> {
    if (Array.isArray(next)) {
      // Parallel execution
      (this as any)._parallel = next;
    } else {
      // Sequential execution
      (this as any)._next = next;
    }
    return Array.isArray(next) ? next[0] : next;
  }

  static when<S extends State>(
    this: StepClass<S>,
    cond: (state: S) => boolean,
    target: StepClass<S>,
    label?: string
  ): StepClass<S> {
    const ctor = this as any;
    if (!ctor._branches) ctor._branches = [];
    ctor._branches.push({ cond, target, label });
    // Return the same class to allow chaining multiple .when calls
    return this;
  }

  /** Set maximum retries for this step (default 0). */
  static retries<S extends State>(this: StepClass<S>, n: number): StepClass<S> {
    (this as any)._maxRetries = Math.max(0, n | 0);
    return this;
  }

  /** Route to a handler step if this step ultimately fails after retries. */
  static catch<S extends State>(this: StepClass<S>, errorStep: StepClass<S>): StepClass<S> {
    (this as any)._onError = errorStep;
    return this;
  }

  // ----- Execution (internal runner via recursion) -----
  static async invoke<S extends State>(
    this: StepClass<S>, 
    initial: S,
    options: {
      enableLedger?: boolean;
      ledgerOptions?: {
        collisionWindow?: number;
        enableStackTrace?: boolean;
      };
    } = {}
  ): Promise<{
    result: S;
    state: S;
    path: string[];
    metadata: {
      steps: any[];
      totalDuration: number;
      startTime: number;
      endTime: number;
    };
    ledger?: LedgerLog;
    reconciliation?: {
      conflicts: Array<{
        property: string | symbol;
        operations: any[];
        conflictType: 'overwrite' | 'nested' | 'race';
      }>;
      reconciled: any[];
    };
  }> {
    const enableLedger = options.enableLedger ?? true;
    const ledgerOptions = options.ledgerOptions ?? {};
    
    // Wrap initial state in ledger if enabled
    let state = initial;
    if (enableLedger) {
      state = createLedger(initial, ledgerOptions);
    }
    
    const ctor = this as any as StepClass<S>;
    const instance = new ctor();
    const name = ctor.name || 'Step';
    const start = Date.now();

    // Ensure meta log exists
    (state as any)._meta ??= [];
    const metaEntry: any = { step: name, attempts: 0, start };

    const maxRetries = ((ctor as any)._maxRetries ?? 0) as number;

    let attempt = 0;
    while (true) {
      attempt++;
      metaEntry.attempts = attempt;
      try {
        state = await instance.execute(state);
        metaEntry.ok = true;
        break;
      } catch (err: any) {
        metaEntry.ok = false;
        metaEntry.error = err?.message ?? String(err);
        if (attempt <= maxRetries) {
          // optional simple backoff; adjust as desired
          await new Promise(r => setTimeout(r, Math.min(250 * attempt, 1000)));
          continue; // retry
        }
        // route to error handler if present
        const onError = (ctor as any)._onError as StepClass<S> | undefined;
        metaEntry.end = Date.now();
        metaEntry.durationMs = metaEntry.end - metaEntry.start;
        (state as any)._meta.push(metaEntry);
        if (onError) {
          return onError.invoke(state, options);
        }
        throw err;
      }
    }

    metaEntry.end = Date.now();
    metaEntry.durationMs = metaEntry.end - metaEntry.start;
    (state as any)._meta.push(metaEntry);

    // Determine next step: evaluate branches in order, else default .then
    const branches = (ctor as any)._branches as Branch<S>[] | undefined;
    if (branches && branches.length) {
      for (const b of branches) {
        let chosen = false;
        try {
          chosen = !!b.cond(state);
        } catch {
          // ignore condition errors; treat as false
        }
        if (chosen) {
          return (b.target as StepClass<S>).invoke(state, options);
        }
      }
    }

    // Check for parallel execution first
    const parallel = (ctor as any)._parallel as StepClass<S>[] | undefined;
    if (parallel && parallel.length > 0) {
      // Execute all parallel steps concurrently
      const parallelPromises = parallel.map(stepClass => 
        stepClass.invoke(state, options)
      );
      
      const parallelResults = await Promise.all(parallelPromises);
      
      // Merge results - take the last result as the final state
      // In a real implementation, you might want more sophisticated merging
      const finalResult = parallelResults[parallelResults.length - 1];
      
      // Combine all ledgers and reconciliations
      const allLedgers = parallelResults
        .map(r => r.ledger)
        .filter(Boolean);
      
      const allReconciliations = parallelResults
        .map(r => r.reconciliation)
        .filter(Boolean);
      
      if (allLedgers.length > 0) {
        finalResult.ledger = allLedgers[0]; // Use first ledger for now
        finalResult.reconciliation = allReconciliations[0];
      }
      
      // Clear the parallel flag to prevent infinite recursion
      (ctor as any)._parallel = undefined;
      
      return finalResult;
    }
    
    // Check for sequential execution
    const next = (ctor as any)._next as StepClass<S> | undefined;
    if (next) {
      return next.invoke(state, options);
    }

    // Extract clean result (excluding internal properties and ledger)
    const { _meta, path, [LEDGER_SYMBOL]: ledgerSymbol, ...cleanResult } = state as any;
    const result = (state as any).result !== undefined ? (state as any).result : cleanResult;
    
    // Extract path for separate access
    const executionPath = (state as any).path || [];
    
    // Create metadata object
    const endTime = Date.now();
    const metadata = {
      steps: (state as any)._meta || [],
      totalDuration: endTime - start,
      startTime: start,
      endTime: endTime
    };

    // Extract ledger information if available
    let ledger: LedgerLog | undefined;
    let reconciliation: any = undefined;
    
    if (enableLedger && (state as any)[LEDGER_SYMBOL]) {
      ledger = (state as any)[LEDGER_SYMBOL];
      // Automatically reconcile the ledger to detect conflicts
      reconciliation = reconcileLedger(state as any);
    }

    // Return structured result
    return {
      result,
      state,
      path: executionPath,
      metadata,
      ledger,
      reconciliation
    };
  }

  // ----- Mermaid graph generation -----
  static graph<S extends State>(
    this: StepClass<S>,
    opts?: { direction?: 'TD' | 'LR' }
  ): string {
    const dir = opts?.direction ?? 'TD';
    const lines: string[] = [`graph ${dir}`];

    const seen = new Set<StepClass<S>>();
    const visit = (cls: StepClass<S>) => {
      if (seen.has(cls)) return;
      seen.add(cls);

      const name = (cls as any).name || 'Step';
      const branches = ((cls as any)._branches ?? []) as Branch<S>[];
      const next = (cls as any)._next as StepClass<S> | undefined;

      // Emit branch edges first (with optional labels)
      for (const b of branches) {
        const targetName = ((b.target as any).name) || 'Step';
        const label = b.label ? `|${escapeLabel(b.label)}|` : '';
        lines.push(`  ${name} -->${label} ${targetName}`);
        visit(b.target);
      }

      // Emit default next edge
      if (next) {
        const targetName = ((next as any).name) || 'Step';
        lines.push(`  ${name} --> ${targetName}`);
        visit(next);
      }

      // If no outgoing edges, you can mark it as terminal (optional)
      if (!branches.length && !next) {
        // No-op; Mermaid infers terminal nodes
      }
    };

    visit(this as StepClass<S>);
    return lines.join('\n');
  }
}

function escapeLabel(s: string): string {
  return s.replace(/\|/g, '\\|');
}
